<template>
  <div class="ps-update-page">
    <a-spin :spinning="pageLoading">
      <div class="ps-form-layout">
        <aside class="ps-sidebar">
          <div class="ps-side-card">
            <div class="ps-side-title">填写步骤</div>
            <button
              v-for="(mod, i) in modules"
              :key="mod.key"
              type="button"
              class="ps-step-item"
              :class="{ active: i === editStep, 'has-change': moduleChanged(mod.key) }"
              @click="editGo(i)"
            >
              <span class="ps-step-label">
                {{ mod.label }}
              </span>
              <span class="ps-step-mark">{{ i === editStep ? "✎" : moduleChanged(mod.key) ? moduleTotal(mod.key) : "" }}</span>
            </button>
            <div class="ps-side-hint">
               点击步骤切换模块，带 <b style="color: #ef4444">*</b> 的为必填项
            </div>
          </div>
        </aside>

        <div class="ps-content">
          <div class="ps-status-bar">
            <div class="ps-status-block">
              <span class="ps-status-label">当前状态</span>
              <StatusBadge :status="project.auditState" />
            </div>
            <div class="ps-status-block">
              <span class="ps-status-label">正在编辑</span>
              <span class="ps-status-value warn">{{ editingStatusText }}</span>
            </div>
            <div
              v-if="baseChangeDiffs.length"
              class="ps-status-block ps-status-block--click"
              @click="editStep = 0"
            >
              <span class="ps-status-label">基本信息</span>
              <span class="ps-status-value warn">{{ baseChangeDiffs.length }} 处{{ baseLocked ? "待审核" : "草稿" }}变更</span>
            </div>
            <div
              v-if="resourceChangeDiffs.length"
              class="ps-status-block ps-status-block--click"
              @click="editStep = 2"
            >
              <span class="ps-status-label">补充材料</span>
              <span class="ps-status-value warn">{{ resourceChangeDiffs.length }} 处{{ resourceLocked ? "待审核" : "草稿" }}变更</span>
            </div>
            <div style="flex: 1"></div>
            <button type="button" class="ps-btn ps-btn--white" :disabled="saving" @click="saveDraft">
              💾 保存草稿
            </button>
            <button type="button" class="ps-btn ps-btn--blue" :disabled="saving" @click="submitAudit">
              ✉ 提交变更审核
            </button>
            <!-- <button
              type="button"
              class="ps-btn ps-btn--outline-orange"
              @click="openCompare"
            >
              ⇄ 对比上一版
            </button> -->
            <button type="button" class="ps-btn ps-btn--white" @click="goBack">
              ↩ 返回
            </button>
          </div>

          <div class="ps-form-card" :class="{ 'is-origin-form': currentKey === 'base' }">
            <!-- 基本信息：沿用特色文化育人品牌「品牌基本信息」样式 -->
            <div v-show="currentKey === 'base'" class="card-item origin-base-form">
              <div class="title-box">
                <span class="title-text">{{ currentModule.label }}</span>
                <span class="ps-form-hint">⚠ 已从当前已通过版本自动复制，绿色为本次变更项</span>
              </div>
              <div v-if="baseLocked" class="ps-lock-banner">
                基本信息已提交审核，当前不可修改。请先撤销后再编辑。
                <button
                  type="button"
                  class="ps-btn ps-btn--outline-orange"
                  :disabled="saving"
                  @click="cancelPendingTask('base')"
                >
                  撤销待审核
                </button>
              </div>
              <div v-if="baseChangeDiffs.length" class="ps-draft-panel">
                <div class="ps-draft-panel__title">
                  {{ baseLocked ? "待审核中的基本信息变更" : "本次基本信息草稿变更" }}
                  · {{ baseChangeDiffs.length }} 项
                </div>
                <div
                  v-for="(f, i) in baseChangeDiffs"
                  :key="'bd' + i"
                  class="ps-draft-panel__row"
                >
                  <span class="k">{{ f.name }}</span>
                  <span class="diff-old">{{ f.old }}</span>
                  <span class="ps-preview-arrow">→</span>
                  <span class="diff-new">{{ f.new }}</span>
                </div>
              </div>
              <div class="ps-form-fields" :class="{ 'ps-readonly': baseLocked }">
              <div class="mt30">
                <div class="form-item" :class="{ 'is-changed': isBaseChanged('name') }">
                  <div class="main-text-style">
                    <span style="color: #f6350f">*</span> 思政微课名称
                  </div>
                  <div class="pl16">
                    <a-input
                      class="input-style-2"
                      type="text"
                      placeholder="请输入思政微课名称"
                      :maxLength="100"
                      v-model="form.name"
                    />
                  </div>
                </div>
                <div class="form-item" :class="{ 'is-changed': isBaseChanged('applyYear') }">
                  <div class="main-text-style">
                    <span style="color: #f6350f">*</span> 申报年份
                  </div>
                  <div class="pl16">
                    <a-date-picker
                      placeholder="请选择申报年份"
                      v-model="form.applyYear"
                      :open="yearOpen.applyYear"
                      :locale="locale"
                      format="YYYY"
                      mode="year"
                      class="form-item-right"
                      @openChange="(s) => (yearOpen.applyYear = s)"
                      @panelChange="(v) => onYearChange('applyYear', v)"
                    />
                  </div>
                </div>
              </div>
              <div class="mt30">
                <div class="form-item" :class="{ 'is-changed': isBaseChanged('foundTime') }">
                  <div class="main-text-style">
                    <span style="color: #f6350f">*</span> 创建时间
                  </div>
                  <div class="pl16">
                    <a-date-picker
                      class="form-item-right"
                      placeholder="请选择创建时间"
                      v-model="form.foundTime"
                      :locale="locale"
                      format="YYYY-MM-DD"
                    />
                  </div>
                </div>
              </div>
              <div class="mt30">
                <div class="form-item" style="width: 100%" :class="{ 'is-changed': isMajorChanged }">
                  <div class="main-text-style">
                    <span style="color: #f6350f">*</span> 所属专业
                  </div>
                  <div
                    class="pl16"
                    style="display: flex; justify-content: space-between; align-items: center"
                  >
                    <a-select
                      style="width: 32%"
                      show-search
                      v-model="form.basicMajorCategory"
                      option-filter-prop="children"
                      :filter-option="filterOption"
                      @change="onCategoryChange"
                    >
                      <a-select-option value="">请选择专业大类</a-select-option>
                      <a-select-option v-for="item in zydlList" :key="item.id" :value="item.id">
                        {{ item.name }}
                      </a-select-option>
                    </a-select>
                    <a-select
                      style="width: 32%"
                      show-search
                      v-model="form.basicMajorSubclass"
                      option-filter-prop="children"
                      :filter-option="filterOption"
                      @change="onSubclassChange"
                    >
                      <a-select-option value="">请选择专业子类</a-select-option>
                      <a-select-option v-for="item in zyzlList" :key="item.id" :value="item.id">
                        {{ item.name }}
                      </a-select-option>
                    </a-select>
                    <a-select
                      style="width: 32%"
                      show-search
                      v-model="form.basicMajor"
                      option-filter-prop="children"
                      :filter-option="filterOption"
                    >
                      <a-select-option value="">请选择专业</a-select-option>
                      <a-select-option v-for="item in zyList" :key="item.id" :value="item.id">
                        {{ item.name }}
                      </a-select-option>
                    </a-select>
                  </div>
                </div>
              </div>
              <div class="mt30">
                <div class="form-item" style="width: 100" :class="{ 'is-changed': isBaseChanged('course') }">
                  <div class="main-text-style">
                    <span style="color: #f6350f">*</span> 所属课程
                  </div>
                  <div class="pl16">
                    <a-select
                      mode="tags"
                      style="width: 100%;"
                      class="course-tags-select"
                      v-model="form.course"
                      placeholder="请选择课程"
                    >
                      <a-select-option
                        v-for="item in kcList"
                        :key="item.courseName"
                        :value="item.courseName"
                      >
                        {{ item.courseName }}
                      </a-select-option>
                    </a-select>
                  </div>
                </div>
              </div>
              <div class="mt30">
                <div class="form-item" :class="{ 'is-changed': isBaseChanged('courseType') }">
                  <div class="main-text-style">
                    <span style="color: #f6350f">*</span> 课程类型
                  </div>
                  <div class="pl16">
                    <a-select
                      style="width: 100%"
                      v-model="form.courseType"
                      placeholder="请选择课程类型"
                    >
                      <a-select-option
                        v-for="item in kclists"
                        :key="item.value"
                        :value="item.value"
                      >
                        {{ item.title }}
                      </a-select-option>
                    </a-select>
                  </div>
                </div>
                <div class="form-item" :class="{ 'is-changed': isBaseChanged('wkStudyNum') }">
                  <div class="main-text-style">
                    <span style="color: #f6350f">*</span> 学习人次
                  </div>
                  <div class="pl16">
                    <a-input-number
                      class="input-style-2"
                      style="padding: 0px 20px; width: 100%;"
                      v-model="form.wkStudyNum"
                      :min="0"
                      placeholder="请输入学习人次"
                    />
                  </div>
                </div>
              </div>
              <div class="mt30">
                <div class="form-item" :class="{ 'is-changed': isBaseChanged('levelType') }">
                  <div class="main-text-style">
                    <span style="color: #f6350f">*</span> 级别
                  </div>
                  <div class="pl16">
                    <a-select v-model="form.levelType" placeholder="请选择级别" style="width: 100%">
                      <a-select-option
                        v-for="item in jblist"
                        :key="item.text"
                        :value="item.text"
                      >
                        {{ item.title }}
                      </a-select-option>
                    </a-select>
                  </div>
                </div>
              </div>
              <div class="mt30">
                <div class="form-item-upload" :class="{ 'is-changed': isFileChanged }">
                  <div class="main-text-style">
                    <span style="color: #f6350f">*</span>
                    申报书上传
                  </div>
                  <div class="pl16">
                    <a-upload
                      :file-list="fileList"
                      accept=".pdf"
                      :disabled="baseLocked"
                      :remove="baseLocked ? () => false : removeDeclaration"
                      :before-upload="beforeDeclaration"
                    >
                      <a-button :disabled="baseLocked">
                        <a-icon type="upload" />上传附件
                      </a-button>
                      <span class="text2">注：请上传pdf格式的文件</span>
                    </a-upload>
                  </div>
                </div>
              </div>
              </div>
            </div>

            <!-- 团队成员：沿用原填报表格 -->
            <div v-show="currentKey === 'team'" class="ps-team-embed">
              <team-info
                v-if="project.id"
                ref="teamInfo"
                :old-infos="editInfo"
                :isbj="false"
                :show-change-status="true"
              />
            </div>

            <!-- 补充材料 -->
            <div v-show="currentKey === 'material'" class="ps-grid-2 card-item origin-base-form">
              <!-- <div class="ps-grid-full ps-form-head">
                <h3 class="ps-section-title" style="margin: 0">{{ currentModule.label }}</h3>
                <span class="ps-form-hint">⚠ 绿色为本次变更项</span>
              </div> -->
             <div class="title-box ps-grid-full">
                <span class="title-text">{{ currentModule.label }}</span>
                <span class="ps-form-hint">⚠ 绿色为本次变更项</span>
              </div>
              <div v-if="resourceLocked" class="ps-lock-banner ps-grid-full">
                补充材料已提交审核，当前不可修改。请先撤销后再编辑。
                <button
                  type="button"
                  class="ps-btn ps-btn--outline-orange"
                  :disabled="saving"
                  @click="cancelPendingTask('material')"
                >
                  撤销待审核
                </button>
              </div>
              <div v-if="resourceChangeDiffs.length" class="ps-draft-panel ps-grid-full">
                <div class="ps-draft-panel__title">
                  {{ resourceLocked ? "待审核中的补充材料变更" : "本次补充材料草稿变更" }}
                  · {{ resourceChangeDiffs.length }} 项
                </div>
                <div
                  v-for="(f, i) in resourceChangeDiffs"
                  :key="'rd' + i"
                  class="ps-draft-panel__row"
                >
                  <span class="k">{{ f.name }}</span>
                  <span class="diff-old">{{ f.old }}</span>
                  <span class="ps-preview-arrow">→</span>
                  <span class="diff-new">{{ f.new }}</span>
                </div>
              </div>
              <div class="ps-field ps-grid-full" :class="{ 'is-changed': isResChanged('type'), 'ps-readonly': resourceLocked }">
                <label><span class="req">*</span> 补充视频类型</label>
                <a-radio-group v-model="resource.type" :disabled="resourceLocked" @change="onResTypeChange">
                  <a-radio value="0">本地视频</a-radio>
                  <a-radio value="1">超链接</a-radio>
                </a-radio-group>
              </div>
              <template v-if="String(resource.type) === '0'">
                <div class="ps-field ps-grid-full" :class="{ 'is-changed': isResChanged('name') }">
                  <label><span class="req">*</span> 思政微课视频</label>
                  <a-upload
                    :file-list="videoList"
                    accept=".mp4"
                    :disabled="videoUploading || resourceLocked"
                    :remove="resourceLocked ? () => false : removeVideo"
                    :before-upload="beforeVideo"
                  >
                    <button type="button" class="ps-upload-btn" :disabled="videoUploading || resourceLocked">
                      <a-icon type="upload" /> {{ videoUploading ? "上传中..." : "上传附件" }}
                    </button>
                    <span class="ps-file-note">请上传视频</span>
                  </a-upload>
                </div>
              </template>
              <template v-else>
                <div class="ps-field" :class="{ 'is-changed': isResChanged('name') }">
                  <label><span class="req">*</span> 超链接名称</label>
                  <a-input v-model="resource.name" placeholder="请输入超链接名称" :disabled="resourceLocked" />
                </div>
                <div class="ps-field" :class="{ 'is-changed': isResChanged('filePath') }">
                  <label><span class="req">*</span> 思政微课超链接</label>
                  <a-input v-model="resource.filePath" placeholder="请输入超链接地址" :disabled="resourceLocked" />
                </div>
              </template>
              <div class="ps-field ps-grid-full" :class="{ 'is-changed': isResChanged('ideologicalElement') }">
                <label><span class="req">*</span> 思政元素</label>
                <a-select
                  v-model="resource.ideologicalElement"
                  mode="tags"
                  placeholder="请选择思政元素"
                  style="width: 100%"
                  :disabled="resourceLocked"
                >
                  <a-select-option v-for="item in ysList" :key="item.name" :value="item.name">
                    {{ item.name }}
                  </a-select-option>
                </a-select>
              </div>
            </div>
          </div>

          <div class="ps-footer-bar">
            <div class="ps-footer-tip">
              💡 更新时系统自动复制最新已通过版本，只需修改<b>变动项</b>即可
            </div>
            <div class="ps-footer-actions">
              <button type="button" class="ps-btn ps-btn--white" @click="editPrev">
                ← 上一步
              </button>
              <button type="button" class="ps-btn ps-btn--blue" @click="editNext">
                下一步 →
              </button>
              <button type="button" class="ps-btn ps-btn--blue" :disabled="saving" @click="submitAudit">
                ✉ 提交变更审核
              </button>
            </div>
          </div>
        </div>
      </div>
    </a-spin>

    <div class="ps-modal-mask" :class="{ open: compareVisible }" @click.self="compareVisible = false">
      <div class="ps-modal">
        <div class="ps-modal-head">
          <div>
            <h3>版本对比</h3>
            <p>
              左：上一版本　右：变更后　<span style="color: #b91c1c">红＝删除</span>　<span style="color: #15803d">绿＝新增</span>
            </p>
          </div>
          <button type="button" class="ps-close-btn" @click="compareVisible = false">✕</button>
        </div>
        <div class="ps-modal-body">
          <div class="ps-compare-grid">
            <div>
              <div class="ps-cmp-head gray">已通过版本</div>
              <div class="ps-cmp-body">
                <div v-for="(f, i) in compareFields" :key="'l' + i" class="ps-cmp-line">
                  <span class="k">{{ f.name }}：</span>
                  <span v-if="f.changed" class="diff-old">{{ f.old }}</span>
                  <span v-else>{{ f.old }}</span>
                </div>
                <div v-if="!compareFields.length" class="ps-cmp-line">（暂无变更字段）</div>
              </div>
            </div>
            <div>
              <div class="ps-cmp-head blue">变更后</div>
              <div class="ps-cmp-body blue-border">
                <div v-for="(f, i) in compareFields" :key="'r' + i" class="ps-cmp-line">
                  <span class="k">{{ f.name }}：</span>
                  <span v-if="f.changed" class="diff-new">{{ f.new }}</span>
                  <span v-else>{{ f.new }}</span>
                </div>
                <div v-if="!compareFields.length" class="ps-cmp-line">（暂无变更字段）</div>
              </div>
            </div>
          </div>
          <div v-if="compareFields.filter((f) => f.changed).length" class="ps-change-alert">
            ⚠ 检测到
            <b>{{ compareFields.filter((f) => f.changed).length }}</b>
            处变更。管理员将重点审核这些字段。
          </div>
        </div>
        <div class="ps-modal-foot">
          <button type="button" class="ps-btn ps-btn--white" @click="compareVisible = false">
            关闭
          </button>
        </div>
      </div>
    </div>

    <div class="ps-modal-mask" :class="{ open: submitPreviewVisible }" @click.self="closeSubmitPreview">
      <div class="ps-modal ps-modal--preview">
        <div class="ps-modal-head">
          <div>
            <h3>确认本次提交</h3>
            <p>以下是本次将整批提交的变更，未改动的模块不会进入审核。</p>
          </div>
          <button type="button" class="ps-close-btn" @click="closeSubmitPreview">✕</button>
        </div>
        <div class="ps-modal-body">
          <div v-if="!submitPreviewGroups.length" class="ps-cmp-line">暂无待提交变更</div>
          <div v-for="group in submitPreviewGroups" :key="group.type" class="ps-preview-group">
            <div class="ps-preview-group__title">{{ group.title }}</div>
            <div v-for="(item, idx) in group.items" :key="item.id || idx" class="ps-preview-item">
              <div class="ps-preview-item__head">
                <span class="ps-preview-op" :class="'is-' + item.opKey">{{ item.opText }}</span>
                <span class="ps-preview-item__name">{{ item.rowTitle }}</span>
              </div>
              <div v-if="item.fields.length" class="ps-preview-fields">
                <div v-for="(f, fi) in item.fields" :key="f.key + fi" class="ps-preview-field">
                  <span class="k">{{ f.label }}</span>
                  <span v-if="item.opKey === 'INSERT'" class="diff-new">{{ f.after }}</span>
                  <template v-else-if="item.opKey === 'DELETE'">
                    <span class="diff-old">{{ f.before }}</span>
                  </template>
                  <template v-else>
                    <span class="diff-old">{{ f.before }}</span>
                    <span class="ps-preview-arrow">→</span>
                    <span class="diff-new">{{ f.after }}</span>
                  </template>
                </div>
              </div>
              <div v-else class="ps-preview-empty">（无字段明细，按整条记录提交）</div>
            </div>
          </div>
        </div>
        <div class="ps-modal-foot">
          <button type="button" class="ps-btn ps-btn--white" @click="closeSubmitPreview">
            返回修改
          </button>
          <button
            type="button"
            class="ps-btn ps-btn--blue"
            :disabled="submitLoading || !submitPreviewGroups.length"
            @click="confirmSubmitAudit"
          >
            确认整批提交
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import locale from "ant-design-vue/es/date-picker/locale/zh_CN";
import "moment/locale/zh-cn";
moment.locale("zh-cn");
import {
  getAction,
  postAction,
  putAction,
  fileUpdate,
} from "@/api/index";
import StatusBadge from "./StatusBadge.vue";
import teamInfo from "@/components/szMicroClassFileupload/teamInfo.vue";
import { FIELD_LABELS } from "@/components/auditPage/data.js";
import { getDraftChangeList, getModulePendingAuditList } from "@/api/auditTask.js";

const MODULES = [
  { key: "base", taskType: "WKJIBENXINXI", label: "思政微课基本信息" },
  { key: "team", taskType: "WKTUANDUI", label: "团队成员" },
  { key: "material", taskType: "WKSP", label: "补充材料" },
];

export default {
  name: "CourseUpdatePage",
  components: { StatusBadge, teamInfo },
  data() {
    return {
      locale,
      modules: MODULES,
      editStep: 0,
      pageLoading: false,
      saving: false,
      project: {},
      form: {
        name: "",
        applyYear: null,
        foundTime: null,
        basicMajorCategory: undefined,
        basicMajorSubclass: undefined,
        basicMajor: undefined,
        course: [],
        courseType: undefined,
        wkStudyNum: undefined,
        levelType: undefined,
        declarationName: "",
        declarationPath: "",
      },
      originBase: {},
      fileList: [],
      zydlList: [],
      zyzlList: [],
      zyList: [],
      kcList: [],
      kclists: [],
      jblist: [],
      yearOpen: { applyYear: false },
      teamList: [],
      originTeam: [],
      teamLoading: false,
      resource: {
        type: "0",
        name: "",
        filePath: "",
        aliId: "",
        ideologicalElement: [],
        id: "",
      },
      originResource: {},
      videoList: [],
      videoUploading: false,
      ysList: [],
      resourceTaskId: "",
      baseTaskId: "",
      baseTaskState: "",
      resourceTaskState: "",
      compareVisible: false,
      submitPreviewVisible: false,
      submitLoading: false,
      draftTasks: [],
      pendingList: [],
    };
  },
  computed: {
    applyId() {
      return this.$route.query.id || "";
    },
    currentModule() {
      return this.modules[this.editStep] || this.modules[0];
    },
    currentKey() {
      return this.currentModule.key;
    },
    editInfo() {
      const p = this.project || {};
      return { ...p, id: p.id || this.applyId };
    },
    baseLocked() {
      return String(this.baseTaskState) === "1";
    },
    resourceLocked() {
      return String(this.resourceTaskState) === "1";
    },
    hasPendingTeam() {
      return (this.teamList || []).some((r) => String(r.status) === "1");
    },
    editingStatusText() {
      if (this.baseLocked || this.resourceLocked || this.hasPendingTeam) return "含待审核变更";
      return "变更草稿";
    },
    isMajorChanged() {
      return (
        this.isBaseChanged("basicMajorCategory") ||
        this.isBaseChanged("basicMajorSubclass") ||
        this.isBaseChanged("basicMajor")
      );
    },
    isFileChanged() {
      return this.norm(this.form.declarationName) !== this.norm(this.originBase.declarationName);
    },
    baseChangeDiffs() {
      const list = [];
      const push = (name, oldV, newV) => {
        const o = this.display(oldV);
        const n = this.display(newV);
        if (o !== n) list.push({ name, old: o, new: n });
      };
      push("思政微课名称", this.originBase.name, this.form.name);
      push("申报年份", this.originBase.applyYearText, this.formatYear(this.form.applyYear));
      push("创建时间", this.originBase.foundTimeText, this.formatDate(this.form.foundTime));
      push(
        "专业大类",
        this.listName(this.zydlList, this.originBase.basicMajorCategory),
        this.listName(this.zydlList, this.form.basicMajorCategory)
      );
      push(
        "专业子类",
        this.listName(this.zyzlList, this.originBase.basicMajorSubclass),
        this.listName(this.zyzlList, this.form.basicMajorSubclass)
      );
      push(
        "专业",
        this.listName(this.zyList, this.originBase.basicMajor),
        this.listName(this.zyList, this.form.basicMajor)
      );
      push("所属课程", this.originBase.courseText, this.courseText(this.form.course));
      push(
        "课程类型",
        this.dictName(this.kclists, this.originBase.courseType),
        this.dictName(this.kclists, this.form.courseType)
      );
      push("学习人次", this.originBase.wkStudyNum, this.form.wkStudyNum);
      push(
        "级别",
        this.dictName(this.jblist, this.originBase.levelType),
        this.dictName(this.jblist, this.form.levelType)
      );
      push("申报书", this.originBase.declarationName, this.form.declarationName);
      return list;
    },
    resourceChangeDiffs() {
      const list = [];
      const push = (name, oldV, newV) => {
        const o = this.display(oldV);
        const n = this.display(newV);
        if (o !== n) list.push({ name, old: o, new: n });
      };
      push(
        "视频类型",
        String(this.originResource.type) === "1" ? "超链接" : "本地视频",
        String(this.resource.type) === "1" ? "超链接" : "本地视频"
      );
      push("视频/链接名称", this.originResource.name, this.resource.name);
      push("超链接地址", this.originResource.filePath, this.resource.filePath);
      push(
        "思政元素",
        this.courseText(this.originResource.ideologicalElement),
        this.courseText(this.resource.ideologicalElement)
      );
      return list;
    },
    compareFields() {
      const list = [];
      const push = (name, oldV, newV) => {
        const o = this.display(oldV);
        const n = this.display(newV);
        list.push({ name, old: o, new: n, changed: o !== n });
      };
      push("思政微课名称", this.originBase.name, this.form.name);
      push("申报年份", this.originBase.applyYearText, this.formatYear(this.form.applyYear));
      push("创建时间", this.originBase.foundTimeText, this.formatDate(this.form.foundTime));
      push("所属课程", this.originBase.courseText, this.courseText(this.form.course));
      push("课程类型", this.originBase.courseType, this.form.courseType);
      push("学习人次", this.originBase.wkStudyNum, this.form.wkStudyNum);
      push("级别", this.originBase.levelType, this.form.levelType);
      push("申报书", this.originBase.declarationName, this.form.declarationName);
      push("团队人数", this.originTeam.length, this.teamList.length);
      push(
        "视频类型",
        String(this.originResource.type) === "1" ? "超链接" : "本地视频",
        String(this.resource.type) === "1" ? "超链接" : "本地视频"
      );
      push("视频/链接名称", this.originResource.name, this.resource.name);
      push(
        "思政元素",
        this.courseText(this.originResource.ideologicalElement),
        this.courseText(this.resource.ideologicalElement)
      );
      return list.filter((f) => f.changed);
    },
    submitPreviewGroups() {
      const order = ["WKJIBENXINXI", "WKTUANDUI", "WKSP"];
      const titles = {
        WKJIBENXINXI: "思政微课基本信息",
        WKTUANDUI: "团队成员",
        WKSP: "补充材料",
      };
      const grouped = {};
      (this.draftTasks || []).forEach((task) => {
        const type = task.taskType || "_";
        if (!grouped[type]) grouped[type] = [];
        grouped[type].push(this.buildPreviewItem(task));
      });
      const result = order
        .filter((t) => grouped[t] && grouped[t].length)
        .map((t) => ({ type: t, title: titles[t] || t, items: grouped[t] }));
      Object.keys(grouped).forEach((t) => {
        if (order.indexOf(t) >= 0) return;
        result.push({ type: t, title: t, items: grouped[t] });
      });
      return result;
    },
    moduleChangeMap() {
      const map = {};
      (this.pendingList || []).forEach((t) => {
        const s = String(t.auditState);
        // 基本信息模块的已驳回(3)不展示数量；其余模块草稿(0)、待审核(1)、已驳回(3)均计入
        if (t.taskType === "WKJIBENXINXI") {
          if (s !== "0" && s !== "1") return;
        } else if (s !== "0" && s !== "1" && s !== "3") {
          return;
        }
        const type = t.taskType || "_";
        map[type] = (map[type] || 0) + 1;
      });
      return map;
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    async init() {
      if (!this.applyId) {
        this.$message.error("缺少项目 ID");
        return;
      }
      this.pageLoading = true;
      try {
        await Promise.all([this.loadDicts(), this.loadProject()]);
        await Promise.all([this.loadTeam(), this.loadResource()]);
        await this.applyPendingChanges();
      } finally {
        this.pageLoading = false;
      }
    },
    loadDicts() {
      return Promise.all([
        getAction("/infrastructure/basicMajorCategory/allList", {}).then((res) => {
          if (res.code === 200) this.zydlList = res.result || [];
        }),
        getAction("/lxBase/lxWkCourse/list", {}).then((res) => {
          if (res.code === 200) this.kcList = (res.result && res.result.records) || [];
        }),
        getAction("/baseCommon/getDictItemsByCode?code=course_type").then((res) => {
          if (res.code === 200) this.kclists = res.result || [];
        }),
        getAction("/baseCommon/getDictItemsByCode?code=achievement_level").then((res) => {
          if (res.code === 200) this.jblist = res.result || [];
        }),
        getAction("/infrastructure/lxIdeologicalElement/list", {}).then((res) => {
          if (res.code === 200) this.ysList = (res.result && res.result.records) || [];
        }),
      ]);
    },
    loadProject() {
      return getAction("/lx/lxApply/queryById", { id: this.applyId }).then((res) => {
        if (res.code !== 200 && res.code !== 0) {
          this.$message.error(res.message || "加载项目失败");
          return;
        }
        const p = res.result || {};
        this.project = p;
        let live = p;
        if (p.changeContent) {
          try {
            live =
              typeof p.changeContent === "string"
                ? JSON.parse(p.changeContent)
                : p.changeContent;
          } catch (e) {
            live = p;
          }
        }
        this.applyBaseToForm(live, true);
        if (live !== p) this.applyBaseToForm(p, false);
        const cat = this.form.basicMajorCategory || p.basicMajorCategory;
        const sub = this.form.basicMajorSubclass || p.basicMajorSubclass;
        if (cat) this.loadSubclass(cat, true);
        if (sub) this.loadMajor(sub, true);
      });
    },
    applyBaseToForm(p, setOrigin) {
      const course = this.toCourseArr(p.course);
      this.form = {
        name: p.name || "",
        applyYear: p.applyYear ? moment(String(p.applyYear), "YYYY") : null,
        foundTime: p.foundTime ? moment(p.foundTime) : p.createTime ? moment(p.createTime) : null,
        basicMajorCategory: p.basicMajorCategory || undefined,
        basicMajorSubclass: p.basicMajorSubclass || undefined,
        basicMajor: p.basicMajor || undefined,
        course,
        courseType: p.courseType || undefined,
        wkStudyNum: p.wkStudyNum,
        levelType: p.levelType || undefined,
        declarationName: p.declarationName || "",
        declarationPath: p.declarationPath || "",
      };
      if (p.declarationName && p.declarationPath) {
        this.fileList = [
          { uid: "1", name: p.declarationName, status: "done", path: p.declarationPath },
        ];
      } else if (setOrigin) {
        this.fileList = [];
      }
      if (setOrigin) {
        this.originBase = {
          name: p.name || "",
          applyYearText: this.formatYear(p.applyYear),
          foundTimeText: this.formatDate(p.foundTime || p.createTime),
          basicMajorCategory: p.basicMajorCategory || "",
          basicMajorSubclass: p.basicMajorSubclass || "",
          basicMajor: p.basicMajor || "",
          courseText: this.courseText(course),
          course: this.courseText(course),
          courseType: p.courseType || "",
          wkStudyNum: p.wkStudyNum,
          levelType: p.levelType || "",
          declarationName: p.declarationName || "",
        };
      }
    },
    applyPendingChanges() {
      if (!this.applyId) return Promise.resolve();
      return getModulePendingAuditList({ applyId: this.applyId })
        .then((res) => {
          if (res.code !== 200 && res.code !== 0) return;
          const list = this.flattenPendingList(res.result);
          this.pendingList = list;
          const base = this.pickModuleTask(list, "WKJIBENXINXI");
          if (base) {
            this.baseTaskId = base.id || "";
            this.baseTaskState = String(base.auditState || "");
            const after = this.parsePreviewJson(base.dataAfter || base.applicationData);
            if (after && Object.keys(after).length) {
              this.applyBaseToForm({ ...this.project, ...after }, false);
              if (after.basicMajorCategory) this.loadSubclass(after.basicMajorCategory, true);
              if (after.basicMajorSubclass) this.loadMajor(after.basicMajorSubclass, true);
            }
          } else {
            this.baseTaskId = "";
            this.baseTaskState = "";
          }
          const mat = this.pickModuleTask(list, "WKSP");
          if (mat) {
            this.resourceTaskId = mat.id || this.resourceTaskId;
            this.resourceTaskState = String(mat.auditState || "");
            const after = this.parsePreviewJson(mat.dataAfter || mat.applicationData);
            if (after && Object.keys(after).length) {
              this.resource = {
                type: after.type === 1 || after.type === "1" ? "1" : "0",
                name: after.name || "",
                filePath: after.filePath || "",
                aliId: after.aliId || "",
                ideologicalElement: this.toCourseArr(after.ideologicalElement),
                id: after.id || this.resource.id || "",
              };
              if (this.resource.type === "0" && (this.resource.name || this.resource.aliId)) {
                this.videoList = [
                  {
                    uid: this.resource.id || "-1",
                    name: this.resource.name || "已上传视频",
                    status: "done",
                    path: this.resource.filePath,
                    aliId: this.resource.aliId,
                  },
                ];
              }
            }
          } else {
            this.resourceTaskState = "";
          }
        })
        .catch(() => {});
    },
    flattenPendingList(map) {
      const list = [];
      Object.keys(map || {}).forEach((key) => {
        (map[key] || []).forEach((item) => {
          list.push({ ...item, taskType: item.taskType || key });
        });
      });
      return list;
    },
    pickModuleTask(list, taskType) {
      const items = (list || []).filter((t) => t.taskType === taskType);
      return (
        items.find((t) => String(t.auditState) === "1") ||
        items.find((t) => String(t.auditState) === "0") ||
        // 已驳回(3)任务也接续原任务编辑，后端会重新打开为草稿，避免重复建任务
        items.find((t) => String(t.auditState) === "3") ||
        null
      );
    },
    // 刷新模块待提交/待审核任务，仅更新角标数据，不覆盖表单
    refreshPendingTasks() {
      if (!this.applyId) return Promise.resolve();
      return getModulePendingAuditList({ applyId: this.applyId })
        .then((res) => {
          if (res.code !== 200 && res.code !== 0) return;
          const list = this.flattenPendingList(res.result);
          this.pendingList = list;
          const base = this.pickModuleTask(list, "WKJIBENXINXI");
          this.baseTaskId = base ? base.id || "" : "";
          this.baseTaskState = base ? String(base.auditState || "") : "";
        })
        .catch(() => {});
    },
    // 侧边栏 key（base/team/material）转模块任务类型
    taskTypeOf(key) {
      const m = this.modules.find((x) => x.key === key);
      return (m && m.taskType) || key;
    },
    moduleCount(key) {
      return this.moduleChangeMap[this.taskTypeOf(key)] || 0;
    },
    moduleChanged(key) {
      if (this.taskTypeOf(key) === "WKJIBENXINXI") {
        return this.baseChangeDiffs.length > 0 || this.moduleCount(key) > 0;
      }
      return this.moduleCount(key) > 0;
    },
    moduleTotal(key) {
      if (this.taskTypeOf(key) === "WKJIBENXINXI") {
        return Math.max(this.baseChangeDiffs.length, this.moduleCount(key));
      }
      return this.moduleCount(key);
    },
    modulePending(key) {
      const type = this.taskTypeOf(key);
      return (this.pendingList || []).some(
        (t) => t.taskType === type && String(t.auditState) === "1"
      );
    },
    cancelPendingTask(kind) {
      const id = kind === "base" ? this.baseTaskId : this.resourceTaskId;
      if (!id) return;
      this.$confirm({
        title: "撤销待审核变更",
        content: "撤销后该条待审核变更会关闭，页面恢复为已通过内容，然后才能重新修改。",
        okText: "确认撤销",
        cancelText: "取消",
        onOk: () =>
          postAction(`/audit/lxAuditTask/cancelTask?ids=${id}`).then((res) => {
            if (res.code === 200) {
              this.$message.success("已撤销，现在可以修改");
              const reload =
                kind === "base" ? this.loadProject() : this.loadResource();
              return reload.then(() => this.applyPendingChanges());
            }
            this.$message.error(res.message || "撤销失败");
          }),
      });
    },
    loadTeam() {
      if (!this.applyId) return Promise.resolve();
      this.teamLoading = true;
      const apply = (res) => {
        if (res.code === 200 || res.code === 0) {
          const r = res.result;
          this.teamList = (r && r.records) || (Array.isArray(r) ? r : []) || [];
          if (!this.originTeam.length) this.originTeam = this.teamList.slice();
        }
      };
      return getAction("/lxBase/lxApplyGroup/allList", {
        applyType: 3,
        applyId: this.applyId,
      })
        .then((res) => {
          apply(res);
          if (!this.teamList.length) {
            return getAction("/lxBase/lxApplyGroup/list", {
              applyType: 3,
              pageNo: 1,
              pageSize: 200,
              applyId: this.applyId,
            }).then(apply);
          }
        })
        .finally(() => {
          this.teamLoading = false;
        });
    },
    loadResource() {
      if (!this.applyId) return Promise.resolve();
      const fill = (r) => {
        if (!r) return;
        let originSrc = null;
        if (r.changeContent) {
          try {
            originSrc =
              typeof r.changeContent === "string"
                ? JSON.parse(r.changeContent)
                : r.changeContent;
          } catch (e) {
            originSrc = null;
          }
        }
        this.resource = {
          type: r.type === 1 || r.type === "1" ? "1" : "0",
          name: r.name || "",
          filePath: r.filePath || "",
          aliId: r.aliId || "",
          ideologicalElement: this.toCourseArr(r.ideologicalElement),
          id: r.id || "",
        };
        this.resourceTaskId = r.taskId || "";
        if (this.resource.type === "0" && (r.name || r.aliId)) {
          this.videoList = [
            {
              uid: r.id || "-1",
              name: r.name || "已上传视频",
              status: "done",
              path: r.filePath,
              aliId: r.aliId,
            },
          ];
        } else {
          this.videoList = [];
        }
        if (!this.originResource.name && !this.originResource.aliId) {
          const src = originSrc || this.resource;
          this.originResource = {
            type: src.type === 1 || src.type === "1" ? "1" : "0",
            name: src.name || "",
            filePath: src.filePath || "",
            aliId: src.aliId || "",
            ideologicalElement: this.toCourseArr(src.ideologicalElement),
          };
        }
      };
      return getAction("/lxBase/lxApplyResource/allList", {
        applyId: this.applyId,
        applyType: 3,
      }).then((res) => {
        if ((res.code === 200 || res.code === 0) && res.result) {
          fill(res.result);
        } else {
          return getAction("/lxBase/lxApplyResource/getResourceByApplyId", {
            applyId: this.applyId,
          }).then((r2) => {
            if ((r2.code === 200 || r2.code === 0) && r2.result) fill(r2.result);
          });
        }
      });
    },
    loadSubclass(id, keep) {
      return getAction("/infrastructure/basicMajorSubclass/queryByCategoryId", {
        gategoryId: id,
      }).then((res) => {
        if (res.code === 200) this.zyzlList = res.result || [];
        if (!keep) {
          this.form.basicMajorSubclass = undefined;
          this.form.basicMajor = undefined;
          this.zyList = [];
        }
      });
    },
    loadMajor(id, keep) {
      return getAction("/infrastructure/basicMajor/queryBySubclassId", {
        subclassId: id,
      }).then((res) => {
        if (res.code === 200) this.zyList = res.result || [];
        if (!keep) this.form.basicMajor = undefined;
      });
    },
    onCategoryChange(v) {
      this.loadSubclass(v, false);
    },
    onSubclassChange(v) {
      this.loadMajor(v, false);
    },
    onYearChange(key, value) {
      this.form[key] = value;
      this.yearOpen[key] = false;
    },
    filterOption(input, option) {
      return (
        option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0
      );
    },
    norm(v) {
      if (v === null || v === undefined || v === "null") return "";
      return String(v);
    },
    display(v) {
      const s = this.norm(v);
      return s === "" ? "-" : s;
    },
    listName(list, id) {
      if (id === null || id === undefined || id === "") return "-";
      const hit = (list || []).find((x) => String(x.id) === String(id));
      if (!hit) return this.display(id);
      return hit.name || hit.categoryName || hit.subclassName || hit.majorName || this.display(id);
    },
    dictName(list, val) {
      if (val === null || val === undefined || val === "") return "-";
      const hit = (list || []).find(
        (x) =>
          String(x.value) === String(val) ||
          String(x.itemValue) === String(val) ||
          String(x.id) === String(val)
      );
      if (!hit) return this.display(val);
      return hit.text || hit.itemText || hit.title || hit.name || this.display(val);
    },
    formatYear(v) {
      if (!v) return "-";
      if (typeof v === "string" && /^\d{4}$/.test(v.trim())) return v.trim();
      const d = moment(v);
      return d.isValid() ? String(d.year()) : String(v);
    },
    formatDate(v) {
      if (!v) return "-";
      const d = moment(v);
      return d.isValid() ? d.format("YYYY-MM-DD") : String(v);
    },
    toCourseArr(v) {
      if (Array.isArray(v)) return v.filter(Boolean);
      if (typeof v === "string" && v && v !== "null") {
        return v.split(",").filter(Boolean);
      }
      return [];
    },
    courseText(v) {
      const arr = this.toCourseArr(v);
      return arr.length ? arr.join("、") : "";
    },
    isBaseChanged(key) {
      if (key === "applyYear") {
        return this.formatYear(this.form.applyYear) !== this.norm(this.originBase.applyYearText);
      }
      if (key === "foundTime") {
        return this.formatDate(this.form.foundTime) !== this.norm(this.originBase.foundTimeText);
      }
      if (key === "course") {
        return this.courseText(this.form.course) !== this.norm(this.originBase.courseText);
      }
      return this.norm(this.form[key]) !== this.norm(this.originBase[key]);
    },
    isResChanged(key) {
      if (key === "ideologicalElement") {
        return (
          this.courseText(this.resource.ideologicalElement) !==
          this.courseText(this.originResource.ideologicalElement)
        );
      }
      return this.norm(this.resource[key]) !== this.norm(this.originResource[key]);
    },
    editGo(i) {
      this.editStep = i;
      this.refreshPendingTasks();
    },
    editNext() {
      if (this.editStep < this.modules.length - 1) {
        this.editStep += 1;
        this.refreshPendingTasks();
      } else this.$message.info("已是最后一个模块");
    },
    editPrev() {
      if (this.editStep > 0) {
        this.editStep -= 1;
        this.refreshPendingTasks();
      } else this.$message.info("已是第一个模块");
    },
    goBack() {
      this.$router.push({
        name: "szMicroClassProjectDetail",
        query: {
          id: this.applyId,
          applyType: "3",
          name: this.project.name,
          tab: "current",
        },
      });
    },
    beforeDeclaration(file) {
      if (this.baseLocked) {
        this.$message.warning("基本信息待审核中，请先撤销后再修改");
        return false;
      }
      if (this.fileList.length >= 1) {
        this.$message.error("申报书只能上传一个文件,请移除后再上传");
        return false;
      }
      const formData = new FormData();
      formData.append("file", file);
      formData.append("biz", "linxuan");
      fileUpdate(formData).then((res) => {
        if (res.code === 0) {
          this.$message.success("上传成功");
          this.fileList = [{ uid: file.uid, name: file.name, status: "done", path: res.message }];
          this.form.declarationName = file.name;
          this.form.declarationPath = res.message;
        } else {
          this.$message.error("上传失败");
        }
      });
      return false;
    },
    removeDeclaration() {
      this.fileList = [];
      this.form.declarationName = "";
      this.form.declarationPath = "";
    },
    onResTypeChange() {
      if (String(this.resource.type) === "1") {
        this.resource.aliId = "";
      }
    },
    removeVideo() {
      this.videoList = [];
      this.resource.name = "";
      this.resource.filePath = "";
      this.resource.aliId = "";
    },
    beforeVideo(file) {
      if (this.resourceLocked) {
        this.$message.warning("补充材料待审核中，请先撤销后再修改");
        return false;
      }
      this.handleVideoUpload(file);
      return false;
    },
    handleVideoUpload(file) {
      if (typeof window.AliyunUpload === "undefined") {
        this.$message.error("视频上传组件未加载，请刷新后重试");
        return;
      }
      const self = this;
      this.videoUploading = true;
      const uploader = new window.AliyunUpload.Vod({
        timeout: 60000,
        partSize: 1048576,
        parallel: 5,
        retryCount: 3,
        retryDuration: 2,
        region: "cn-beijing",
        userId: "1104845979036938",
        addFileSuccess() {},
        onUploadstarted(uploadInfo) {
          if (!uploadInfo.videoId) {
            const params = new FormData();
            params.append("FileName", uploadInfo.file.name);
            params.append("Title", uploadInfo.file.name.split(".")[0]);
            params.append("CateId", "11095");
            postAction("/sys/video/CreateUploadVideo", params).then((res) => {
              uploader.setUploadAuthAndAddress(
                uploadInfo,
                res.result.uploadAuth,
                res.result.uploadAddress,
                res.result.videoId
              );
            });
          } else {
            postAction(`/sys/video/refresVideoPlayAuth?videoId=${uploadInfo.videoId}`).then(
              (res) => {
                uploader.setUploadAuthAndAddress(
                  uploadInfo,
                  res.result.uploadAuth,
                  res.result.uploadAddress,
                  res.result.videoId
                );
              }
            );
          }
        },
        onUploadSucceed(uploadInfo) {
          self.videoUploading = false;
          self.resource.aliId = uploadInfo.videoId;
          self.resource.name = uploadInfo.file.name;
          self.resource.filePath = "";
          self.videoList = [
            { uid: uploadInfo.file.uid, name: uploadInfo.file.name, status: "done", aliId: uploadInfo.videoId },
          ];
          self.$message.success("视频上传成功");
        },
        onUploadFailed() {
          self.videoUploading = false;
          self.$message.error("视频上传失败");
        },
      });
      const userData = '{"Vod":{}}';
      uploader.addFile(file, null, null, null, userData);
      uploader.startUpload();
    },
    validateBase() {
      if (!this.form.name) return "请填写思政微课名称";
      if (!this.form.applyYear) return "请选择申报年份";
      if (!this.form.foundTime) return "请选择创建时间";
      if (!this.form.basicMajorCategory) return "请选择专业大类";
      if (!this.form.course || !this.form.course.length) return "请选择课程";
      if (!this.form.courseType) return "请选择课程类型";
      if (this.form.wkStudyNum === "" || this.form.wkStudyNum === undefined) {
        return "请输入学习人次";
      }
      if (!this.form.levelType) return "请选择级别";
      if (!this.fileList.length) return "请上传申报书";
      return "";
    },
    buildBaseParams() {
      const course = Array.isArray(this.form.course)
        ? this.form.course.join(",")
        : this.form.course;
      return {
        applyType: "3",
        name: this.form.name,
        basicMajorCategory: this.form.basicMajorCategory,
        foundTime: this.formatDate(this.form.foundTime),
        basicMajor: this.form.basicMajor,
        basicMajorSubclass: this.form.basicMajorSubclass,
        course,
        courseType: this.form.courseType,
        applyYear: this.formatYear(this.form.applyYear),
        id: this.applyId,
        declarationName: this.fileList[0].name,
        declarationPath: this.fileList[0].path,
        wkStudyNum: this.form.wkStudyNum,
        levelType: this.form.levelType,
      };
    },
    saveBase(silent) {
      if (this.baseLocked) {
        if (!silent) this.$message.warning("基本信息待审核中，请先撤销后再修改");
        return Promise.resolve();
      }
      const err = this.validateBase();
      if (err) {
        if (!silent) this.$message.info(err);
        return Promise.reject({ step: 0, message: err });
      }
      const params = this.buildBaseParams();
      const ok = (res) => {
        if (!silent) this.$message.success("保存成功");
        return res;
      };
      const fail = (res) => {
        this.$message.error((res && res.message) || "保存失败");
        return Promise.reject(res);
      };
      if (this.baseTaskId) {
        return putAction("/audit/lxAuditTask/editTaskAndDetail", {
          id: this.baseTaskId,
          applicationData: JSON.stringify(this.$nullToEmpty(params)),
        }).then((res) => (res.code === 200 ? ok(res) : fail(res)));
      }
      return postAction("/lx/lxApply/wkAdd", params).then((res) => {
        if (res.code === 200) {
          this.baseTaskId = (res.result && res.result.taskId) || "";
          return ok(res);
        }
        return fail(res);
      });
    },
    validateMaterial() {
      if (String(this.resource.type) === "0") {
        if (!this.resource.aliId && !this.videoList.length) return "请上传思政微课视频";
      } else {
        if (!this.resource.name) return "请输入超链接名称";
        if (!this.resource.filePath) return "请输入超链接地址";
      }
      if (!this.resource.ideologicalElement || !this.resource.ideologicalElement.length) {
        return "请添加思政元素";
      }
      return "";
    },
    saveMaterial(silent) {
      if (this.resourceLocked) {
        if (!silent) this.$message.warning("补充材料待审核中，请先撤销后再修改");
        return Promise.resolve();
      }
      const err = this.validateMaterial();
      if (err) {
        if (!silent) this.$message.info(err);
        return Promise.reject({ step: 2, message: err });
      }
      const params = {
        ideologicalElement: this.toCourseArr(this.resource.ideologicalElement).join(),
        applyId: this.applyId,
        id: this.resource.id,
        type: this.resource.type,
        sourceType: this.resource.sourceType || "alioss",
      };
      if (String(this.resource.type) === "0") {
        params.filePath = this.videoList[0] && this.videoList[0].path ? this.videoList[0].path : "";
        params.name = this.resource.name || (this.videoList[0] && this.videoList[0].name) || "";
        params.aliId = this.resource.aliId || "";
      } else {
        params.filePath = this.resource.filePath || "";
        params.name = this.resource.name || "";
        params.aliId = "";
      }
      const ok = (res) => {
        if (!silent) this.$message.success("保存成功");
        return res;
      };
      const fail = (res) => {
        this.$message.error((res && res.message) || "保存失败");
        return Promise.reject(res);
      };
      if (this.resourceTaskId) {
        return putAction("/audit/lxAuditTask/editTaskAndDetail", {
          id: this.resourceTaskId,
          applicationData: JSON.stringify(this.$nullToEmpty(params)),
        }).then((res) => (res.code === 200 ? ok(res) : fail(res)));
      }
      return postAction("/lxBase/lxApplyResource/saveOrUpdate", params).then((res) => {
        if (res.code === 200) {
          if (res.result && res.result.taskId) this.resourceTaskId = res.result.taskId;
          if (res.result && res.result.id) this.resource.id = res.result.id;
          return ok(res);
        }
        return fail(res);
      });
    },
    isBaseDirty() {
      const keys = [
        "name",
        "applyYear",
        "foundTime",
        "basicMajorCategory",
        "basicMajorSubclass",
        "basicMajor",
        "course",
        "courseType",
        "wkStudyNum",
        "levelType",
      ];
      return keys.some((k) => this.isBaseChanged(k)) || this.isFileChanged;
    },
    isMaterialDirty() {
      return (
        this.isResChanged("type") ||
        this.isResChanged("name") ||
        this.isResChanged("filePath") ||
        this.isResChanged("ideologicalElement") ||
        this.norm(this.resource.aliId) !== this.norm(this.originResource.aliId)
      );
    },
    syncTeamFromTable() {
      const table = this.$refs.teamInfo;
      if (table && table.dataList) this.teamList = table.dataList.slice();
    },
    persistDirtyModules() {
      this.syncTeamFromTable();
      let chain = Promise.resolve();
      if (this.isBaseDirty() && !this.baseLocked) chain = chain.then(() => this.saveBase(true));
      if (this.isMaterialDirty() && !this.resourceLocked) chain = chain.then(() => this.saveMaterial(true));
      return chain;
    },
    handlePersistError(err) {
      if (err && err.step != null) {
        this.editStep = err.step;
        this.$message.info(err.message || "请完善必填项后再提交");
        return;
      }
      if (err && err.message) this.$message.error(err.message);
    },
    saveDraft() {
      this.saving = true;
      return this.persistDirtyModules()
        .then(() => {
          this.$message.success("草稿已保存");
          this.refreshPendingTasks();
        })
        .catch((err) => this.handlePersistError(err))
        .finally(() => {
          this.saving = false;
        });
    },
    submitAudit() {
      this.saving = true;
      this.persistDirtyModules()
        .then(() => getDraftChangeList(this.applyId))
        .then((res) => {
          if (res.code !== 200) {
            this.$message.info(res.message || "获取变更清单失败");
            return;
          }
          const list = res.result || [];
          // 已驳回且为最新一轮的数据会随「提交变更」一并重新送审，纳入提交清单
          const rejectRows = (this.pendingList || []).filter(
            (t) => String(t.auditState) === "3"
          );
          if (!list.length && !rejectRows.length) {
            this.$message.info("没有待提交的变更");
            return;
          }
          this.draftTasks = list.concat(rejectRows);
          this.submitPreviewVisible = true;
        })
        .catch((err) => this.handlePersistError(err))
        .finally(() => {
          this.saving = false;
        });
    },
    closeSubmitPreview() {
      this.submitPreviewVisible = false;
    },
    confirmSubmitAudit() {
      if (this.submitLoading || !this.submitPreviewGroups.length) return;
      this.submitLoading = true;
      getAction("/audit/lxAuditTask/submit?applyId=" + this.applyId, {})
        .then((res) => {
          if (res.code === 200) {
            this.submitPreviewVisible = false;
            this.$message.success("已提交，等待学校或教育局整批审核");
            this.$router.push({
              name: "szMicroClassProjectDetail",
              query: {
                id: this.applyId,
                applyType: "3",
                name: this.project.name,
                tab: "requests",
              },
            });
          } else {
            this.$message.info(res.message || "提交失败");
          }
        })
        .finally(() => {
          this.submitLoading = false;
        });
    },
    parsePreviewJson(str) {
      if (!str) return {};
      let v = str;
      if (typeof v === "string") {
        try {
          v = JSON.parse(v);
        } catch (e) {
          return {};
        }
      }
      if (typeof v === "string") {
        try {
          v = JSON.parse(v);
        } catch (e) {
          return {};
        }
      }
      if (!v || typeof v !== "object" || Array.isArray(v)) return {};
      return v;
    },
    previewJsonHasFields(obj) {
      if (!obj || typeof obj !== "object") return false;
      return Object.keys(obj).some((k) => {
        const val = obj[k];
        return val !== null && val !== undefined && val !== "" && val !== "null";
      });
    },
    localDiffToFields(list) {
      return (list || []).map((f) => ({
        key: f.name,
        label: f.name,
        before: f.old,
        after: f.new,
      }));
    },
    displayPreviewVal(val, key) {
      if (val === null || val === undefined || val === "") return "-";
      if (key === "type") {
        if (String(val) === "0") return "本地视频";
        if (String(val) === "1") return "超链接";
      }
      if (key === "foundTime" || key === "birthday") {
        const d = moment(val);
        if (d.isValid()) return d.format("YYYY-MM-DD");
      }
      if (key === "applyYear") return this.formatYear(val);
      if (key === "course" || key === "ideologicalElement") {
        const t = this.courseText(val);
        return t || "-";
      }
      if (Array.isArray(val)) return val.filter(Boolean).join("、") || "-";
      return String(val);
    },
    buildPreviewItem(task) {
      const opKey = task.operation || "UPDATE";
      const opText = { INSERT: "新增", UPDATE: "修改", DELETE: "删除" }[opKey] || opKey;
      if (task.taskType === "WKJIBENXINXI" && this.baseChangeDiffs.length) {
        return {
          id: task.id,
          opKey: opKey === "INSERT" || opKey === "DELETE" ? opKey : "UPDATE",
          opText,
          rowTitle: this.form.name || (this.project && this.project.name) || "思政微课基本信息",
          fields: this.localDiffToFields(this.baseChangeDiffs),
        };
      }
      if (task.taskType === "WKSP" && this.resourceChangeDiffs.length) {
        return {
          id: task.id,
          opKey: opKey === "INSERT" || opKey === "DELETE" ? opKey : "UPDATE",
          opText,
          rowTitle: this.resource.name || "补充材料",
          fields: this.localDiffToFields(this.resourceChangeDiffs),
        };
      }
      let before = this.parsePreviewJson(task.dataTransBefore);
      let after = this.parsePreviewJson(task.datTransAfter);
      if (!this.previewJsonHasFields(before)) before = this.parsePreviewJson(task.dataBefore);
      if (!this.previewJsonHasFields(after)) after = this.parsePreviewJson(task.dataAfter);
      const labels = (FIELD_LABELS[3] && FIELD_LABELS[3][task.taskType]) || {};
      const fields = [];
      Object.keys(labels).forEach((key) => {
        const label = labels[key];
        if (!label) return;
        let oldRaw = before[key];
        let newRaw = after[key];
        if (oldRaw == null || oldRaw === "") oldRaw = before[key + "_dictText"];
        if (newRaw == null || newRaw === "") newRaw = after[key + "_dictText"];
        if (key === "education" || key === "degree") {
          if (oldRaw == null || oldRaw === "") {
            oldRaw =
              before.education ||
              before.degree ||
              before.education_dictText ||
              before.degree_dictText;
          }
          if (newRaw == null || newRaw === "") {
            newRaw =
              after.education ||
              after.degree ||
              after.education_dictText ||
              after.degree_dictText;
          }
        }
        if (key === "fileName") {
          if (oldRaw == null || oldRaw === "") oldRaw = before.name;
          if (newRaw == null || newRaw === "") newRaw = after.name;
        }
        const b = this.displayPreviewVal(oldRaw, key);
        const a = this.displayPreviewVal(newRaw, key);
        if (opKey === "INSERT") {
          if (a !== "-") fields.push({ key, label, before: "-", after: a });
        } else if (opKey === "DELETE") {
          if (b !== "-") fields.push({ key, label, before: b, after: "-" });
        } else if (b !== a) {
          fields.push({ key, label, before: b, after: a });
        }
      });
      const src = opKey === "DELETE" ? before : after;
      const rowTitle = src.teacherName || src.name || src.fileName || "一条记录";
      return {
        id: task.id,
        opKey,
        opText,
        rowTitle,
        fields,
      };
    },
    openCompare() {
      this.syncTeamFromTable();
      this.compareVisible = true;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./updatePage.scss";
</style>
